import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/lib/CartContext";
import { X, Minus, Plus, Trash, ShoppingBagOpen, Lock, ArrowRight } from "@phosphor-icons/react";

const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function CartDrawer() {
  const { open, setOpen, detailedItems, subtotal, updateQty, removeItem, count } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, setOpen]);

  const goCheckout = () => { setOpen(false); navigate("/checkout"); };
  const goCartPage = () => { setOpen(false); navigate("/cart"); };

  return (
    <div
      className={`fixed inset-0 z-[60] overflow-hidden ${open ? "pointer-events-auto" : "pointer-events-none"}`}
      aria-hidden={!open}
      data-testid="cart-drawer"
    >
      <div
        onClick={() => setOpen(false)}
        className={`absolute inset-0 bg-[#080412]/85 backdrop-blur-xl transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0"}`}
        data-testid="cart-drawer-backdrop"
      />
      <aside
        className={`absolute right-0 top-0 h-full w-full max-w-md bg-[#0d0620] border-l border-white/10 flex flex-col transition-transform duration-500 ${open ? "translate-x-0" : "translate-x-full"}`}
        role="dialog"
        aria-label="Shopping cart"
        style={{ boxShadow: "-20px 0 60px rgba(123,44,255,0.25)" }}
      >
        <div className="border-b border-white/10 px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ShoppingBagOpen size={18} weight="duotone" className="text-brandpink" />
            <div className="font-display uppercase text-2xl leading-none text-white">Your Cart</div>
            {count > 0 && (
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">· {count} item{count !== 1 ? "s" : ""}</span>
            )}
          </div>
          <button
            onClick={() => setOpen(false)}
            className="h-9 w-9 grid place-items-center rounded-full border border-white/15 text-white/80 hover:text-white hover:border-white/30 transition-colors"
            data-testid="cart-drawer-close"
            aria-label="Close cart"
          >
            <X size={14} weight="bold" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5">
          {detailedItems.length === 0 ? (
            <div className="text-center py-14">
              <div className="font-display uppercase text-4xl mb-3 text-white">Cart&apos;s <span className="italic font-editorial text-brandpink hr-text-glow-pink">empty.</span></div>
              <p className="text-white/60 text-sm mb-6">
                Add a House Rules box and the party will write itself.
              </p>
              <Link
                to="/"
                onClick={() => setOpen(false)}
                className="hr-cta inline-flex h-11 items-center gap-2 px-5 rounded-full font-sora font-semibold text-[11px] uppercase tracking-[0.22em] text-white"
                data-testid="cart-drawer-shop-link"
              >
                Shop the Box <ArrowRight size={14} />
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {detailedItems.map((item) => (
                <div
                  key={item.product_id}
                  className="flex gap-3 p-3 rounded-2xl border border-white/10 bg-white/[0.03]"
                  data-testid="cart-drawer-item"
                >
                  <img
                    src={item.product.image}
                    alt={item.product.name}
                    className="w-20 h-20 object-cover rounded-xl border border-white/10"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-display uppercase text-lg leading-tight text-white">
                      {item.product.name}
                    </div>
                    <div className="text-xs font-mono text-white/50 mt-1">
                      {fmt(item.product.price)} ea
                    </div>
                    <div className="mt-3 flex items-center justify-between gap-2">
                      <div className="flex items-center rounded-full overflow-hidden border border-white/15 bg-white/[0.03]">
                        <button
                          aria-label="Decrease quantity"
                          onClick={() => updateQty(item.product_id, item.quantity - 1)}
                          className="px-2 py-1 text-white hover:bg-white/8"
                          data-testid="cart-drawer-decrease-qty"
                        >
                          <Minus size={12} weight="bold" />
                        </button>
                        <span className="px-3 font-mono text-sm text-white" data-testid="cart-drawer-item-qty">
                          {item.quantity}
                        </span>
                        <button
                          aria-label="Increase quantity"
                          onClick={() => updateQty(item.product_id, item.quantity + 1)}
                          className="px-2 py-1 text-white hover:bg-white/8"
                          data-testid="cart-drawer-increase-qty"
                        >
                          <Plus size={12} weight="bold" />
                        </button>
                      </div>
                      <div className="font-anton text-xl text-white" data-testid="cart-drawer-line-total">
                        {fmt(item.lineTotal)}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => removeItem(item.product_id)}
                    aria-label="Remove from cart"
                    className="self-start text-white/40 hover:text-brandpink p-1 transition-colors"
                    data-testid="cart-drawer-remove-item"
                  >
                    <Trash size={14} weight="duotone" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {detailedItems.length > 0 && (
          <div className="border-t border-white/10 px-5 py-5 bg-white/[0.02]">
            <div className="flex items-center justify-between mb-1">
              <span className="font-sora text-[10.5px] font-semibold uppercase tracking-[0.24em] text-white/60">Subtotal</span>
              <span className="font-anton text-3xl text-white" data-testid="cart-drawer-subtotal">
                {fmt(subtotal)}
              </span>
            </div>
            <div className="text-xs font-sans text-white/45 mb-4">
              Shipping &amp; taxes at checkout · Free US shipping
            </div>
            <button
              onClick={goCheckout}
              className="hr-cta w-full h-12 rounded-full font-sora font-semibold text-sm uppercase tracking-[0.22em] text-white flex items-center justify-center gap-2"
              data-testid="cart-drawer-checkout-button"
            >
              <Lock size={14} weight="duotone" /> Secure Checkout
            </button>
            <button
              onClick={goCartPage}
              className="w-full mt-4 font-sora text-[10.5px] font-semibold uppercase tracking-[0.24em] text-white/60 hover:text-brandpink transition-colors"
              data-testid="cart-drawer-view-cart"
            >
              View full cart →
            </button>
          </div>
        )}
      </aside>
    </div>
  );
}
